package com.evoapps.evoshift.domain.model

import kotlinx.datetime.DatePeriod
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus
import kotlinx.serialization.Serializable
import kotlin.time.Instant
import kotlin.math.roundToLong

@Serializable
data class Owner(
    val uid: String,
    val businessName: String,
    val subscriptionStatus: SubscriptionStatus,
    val workplaceIds: List<String> = emptyList(),
    /** When the free months began (SH-52); set only by functions/planAccess.js, null until then. */
    val trialStartedAt: Instant? = null
)

@Serializable
enum class SubscriptionStatus {
    ACTIVE, TRIAL, PAST_DUE, CANCELLED
}

@Serializable
data class RecurringCostRule(
    val id: String,
    val name: String,
    val amount: Double,
    val currency: String,
    val frequency: CostFrequency,
    val targetWorkplaceId: String,
    /** SH-87: a cost (rent) or revenue (a sublease); rules saved before are costs. */
    val kind: LedgerKind = LedgerKind.COST
)

/**
 * SH-87: whether a recurring rule or a month's ledger item is money going out (COST) or coming
 * in (REVENUE). Stored as its name; anything else (or nothing, before SH-87) reads as COST.
 */
enum class LedgerKind {
    COST,
    REVENUE;

    companion object {
        fun fromId(id: String?): LedgerKind = entries.firstOrNull { it.name == id } ?: COST
    }
}

enum class CostFrequency {
    MONTHLY,
    WEEKLY,
    YEARLY,
    ONE_OFF
}

/** One charge of a rule in a roster month (the ledger entry under rosters/{id}/costs). */
data class MaterializedCost(val rule: RecurringCostRule, val date: String, val amount: Double)

/**
 * What a rule costs in a month, dated inside that month so daily records show it:
 * MONTHLY once on the 1st, WEEKLY every Monday, YEARLY a twelfth on the 1st (spread over the
 * year), ONE_OFF never (one-off costs are added to a roster directly).
 */
fun RecurringCostRule.occurrencesIn(year: Int, month: Int): List<MaterializedCost> {
    val first = LocalDate(year, month, 1)
    return when (frequency) {
        CostFrequency.MONTHLY -> listOf(MaterializedCost(this, first.toString(), amount))
        CostFrequency.YEARLY -> listOf(MaterializedCost(this, first.toString(), (amount / 12 * 100).roundToLong() / 100.0))
        CostFrequency.WEEKLY -> generateSequence(first) { it.plus(DatePeriod(days = 1)) }
            .takeWhile { it.month == first.month }
            .filter { it.dayOfWeek == DayOfWeek.MONDAY }
            .map { MaterializedCost(this, it.toString(), amount) }
            .toList()
        CostFrequency.ONE_OFF -> emptyList()
    }
}
